import _ from "lodash";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import React from "react";

dayjs.extend(isBetween);
class Utils {
  /**
   * Get first character from first & last sentences of a username
   * @param {String} name - Username
   * @return {String} 2 characters string
   */
  static getNameInitial(name) {
    let initials = name.match(/\b\w/g) || [];
    return ((initials.shift() || "") + (initials.pop() || "")).toUpperCase();
  }

  /**
   * Get current path related object from Navigation Tree
   * @param {Array} navTree - Navigation Tree from directory 'configs/NavigationConfig'
   * @param {String} path - Location path you looking for e.g '/app/dashboards/analytic'
   * @return {Object} object that contained the path string
   */
  static getRouteInfo(navTree, path) {
    if (navTree.path === path) {
      return navTree;
    }
    let route;
    for (let p in navTree) {
      if (navTree.hasOwnProperty(p) && typeof navTree[p] === "object") {
        route = this.getRouteInfo(navTree[p], path);
        if (route) {
          return route;
        }
      }
    }
    return route;
  }

  /**
   * Get accessible color contrast
   * @param {String} hex - Hex color code e.g '#3e82f7'
   * @return {String} 'dark' or 'light'
   */
  static getColorContrast(hex) {
    if (!hex) {
      return "dark";
    }
    const threshold = 130;
    const hRed = hexToR(hex);
    const hGreen = hexToG(hex);
    const hBlue = hexToB(hex);

    function hexToR(h) {
      return parseInt(cutHex(h).substring(0, 2), 16);
    }

    function hexToG(h) {
      return parseInt(cutHex(h).substring(2, 4), 16);
    }

    function hexToB(h) {
      return parseInt(cutHex(h).substring(4, 6), 16);
    }

    function cutHex(h) {
      return h.charAt(0) === "#" ? h.substring(1, 7) : h;
    }

    const cBrightness = (hRed * 299 + hGreen * 587 + hBlue * 114) / 1000;
    if (cBrightness > threshold) {
      return "dark";
    } else {
      return "light";
    }
  }

  /**
   * Darken or lighten a hex color
   * @param {String} color - Hex color code e.g '#3e82f7'
   * @param {Number} percent - Percentage -100 to 100, positive for lighten, negative for darken
   * @return {String} Darken or lighten color
   */
  static shadeColor(color, percent) {
    let R = parseInt(color.substring(1, 3), 16);
    let G = parseInt(color.substring(3, 5), 16);
    let B = parseInt(color.substring(5, 7), 16);
    R = parseInt((R * (100 + percent)) / 100);
    G = parseInt((G * (100 + percent)) / 100);
    B = parseInt((B * (100 + percent)) / 100);
    R = R < 255 ? R : 255;
    G = G < 255 ? G : 255;
    B = B < 255 ? B : 255;
    const RR = R.toString(16).length === 1 ? `0${R.toString(16)}` : R.toString(16);
    const GG = G.toString(16).length === 1 ? `0${G.toString(16)}` : G.toString(16);
    const BB = B.toString(16).length === 1 ? `0${B.toString(16)}` : B.toString(16);
    return `#${RR}${GG}${BB}`;
  }

  /**
   * Convert RGBA to HEX
   * @param {String} rgba - RGBA color code e.g 'rgba(197, 200, 198, .2)')'
   * @return {String} HEX color
   */
  static rgbaToHex(rgba) {
    const trim = (str) => str.replace(/^\s+|\s+$/gm, "");
    const inParts = rgba.substring(rgba.indexOf("(")).split(","),
      r = parseInt(trim(inParts[0].substring(1)), 10),
      g = parseInt(trim(inParts[1]), 10),
      b = parseInt(trim(inParts[2]), 10),
      a = parseFloat(trim(inParts[3].substring(0, inParts[3].length - 1))).toFixed(2);
    const outParts = [
      r.toString(16),
      g.toString(16),
      b.toString(16),
      Math.round(a * 255)
        .toString(16)
        .substring(0, 2),
    ];

    outParts.forEach(function (part, i) {
      if (part.length === 1) {
        outParts[i] = "0" + part;
      }
    });
    return `#${outParts.join("")}`;
  }

  /**
   * Returns either a positive or negative
   * @param {Number} number - number value
   * @param {any} positive - value that return when positive
   * @param {any} negative - value that return when negative
   * @return {any} positive or negative value based on param
   */
  static getSignNum(number, positive, negative) {
    if (number > 0) {
      return positive;
    }
    if (number < 0) {
      return negative;
    }
    return null;
  }

  /**
   * Returns either ascending or descending value
   * @param {Object} a - antd Table sorter param a
   * @param {Object} b - antd Table sorter param b
   * @param {String} key - object key for compare
   * @return {any} a value minus b value
   */
  static antdTableSorter(a, b, key) {
    if (typeof a[key] === "number" && typeof b[key] === "number") {
      return a[key] - b[key];
    }

    if (typeof a[key] === "string" && typeof b[key] === "string") {
      a = a[key].toLowerCase();
      b = b[key].toLowerCase();
      return a > b ? -1 : b > a ? 1 : 0;
    }

    if (typeof a[key] === "boolean" && typeof b[key] === "boolean") {
      a = a[key];
      b = b[key];
      return a === b ? 0 : a ? -1 : 1;
    }
    return;
  }

  /**
   * Filter array of object
   * @param {Array} list - array of objects that need to filter
   * @param {String} key - object key target
   * @param {any} value  - value that excluded from filter
   * @return {Array} a value minus b value
   */
  static filterArray(list, key, value) {
    let data;
    if (!!list) {
      data = list.filter((item) =>
        typeof list[0][key] === "object" ? item[key].includes(value) : item[key] === value,
      );
    }
    return data;
  }

  static filterString(list, key, value) {
    let data;
    if (!!list) {
      data = list.filter((item) => item[key] === value);
    }
    return data;
  }

  /**
   * Remove object from array by value
   * @param {Array} list - array of objects
   * @param {String} key - object key target
   * @param {any} value  - target value
   * @return {Array} Array that removed target object
   */
  static deleteArrayRow(list, key, value) {
    let data = list;
    if (list) {
      data = list.filter((item) => item[key] !== value);
    }
    return data;
  }

  /**
   * Wild card search on all property of the object
   * @param {Number | String} input - any value to search
   * @param {Array} list - array for search
   * @return {Array} array of object contained keyword
   */
  static wildCardSearch(list, input) {
    const searchText = (item) => {
      for (let key in item) {
        if (item[key] == null) {
          continue;
        }
        if (item[key].toString().toUpperCase().indexOf(input.toString().toUpperCase()) !== -1) {
          return true;
        }
      }
    };
    if (!!list) {
      list = list.filter((value) => searchText(value));
      return list;
    } else return list;
  }

  /**
   * Wild card search on product: name, product id and barcode
   * @param {Number | String} input - any value to search
   * @param {Array} list - array for search
   * @param activeCategory
   * @return {Array} array of object contained keyword
   */
  static wildCardSearchProduct(list, input, activeCategory) {
    const query = input.trim().toLowerCase();
    const matches = (value) => value != null && String(value).toLowerCase().includes(query);
    const searchText = (item) => {
      if (!!activeCategory && activeCategory !== "All" && item.productCategory !== activeCategory) return false;
      return matches(item.productName) || matches(item.productId) || matches(item.barcode);
    };
    if (!!list) {
      list = list.filter((value) => searchText(value));
      return list;
    } else return list;
  }

  /**
   * Wild card search on order: Order id, email and first and last name
   * @param {Number | String} input - any value to search
   * @param {Array} list - array for search
   * @return {Array} array of object contained keyword
   */
  static wildCardSearchOnOrder(list, input) {
    const searchText = (item) => {
      const fullName = !!item.farmorderUserFirstName
        ? item.farmorderUserFirstName + " " + item.farmorderUserLastName
        : null;
      if (!!fullName && fullName.toLowerCase().includes(input.toLowerCase())) return true;
      if (item.farmorderId != null && String(item.farmorderId).toLowerCase().includes(input.toLowerCase())) return true;
      if (!!item.id && item.id.toLowerCase().includes(input.toLowerCase())) return true;
      if (item.exactOrderNumber != null && String(item.exactOrderNumber).toLowerCase().includes(input.toLowerCase())) return true;
      if (!!item.farmorderUserId && item.farmorderUserId.toLowerCase().includes(input.toLowerCase())) return true;
      if (!!item.farmorderCustomerName && item.farmorderCustomerName.toLowerCase().includes(input.toLowerCase()))
        return true;
      if (!!item.sooCoolOrderKey && item.sooCoolOrderKey.toLowerCase().includes(input.toLowerCase())) return true;
      return false; // Return false if no match is found
    };
    list = list.filter((value) => searchText(value));
    return list;
  }

  static filterOrdersDateRange(orders, dateRange) {
    let startDate = dateRange[0].format("YYYY-MM-DD");
    let endDate = dateRange[1].format("YYYY-MM-DD");
    return _.filter(orders, function (order) {
      return dayjs(order.farmorderDeliveryDate).isBetween(startDate, endDate, undefined, "[]");
    });
  }
  static wildCardSearchOnSnelstartOrder(list, input) {
    const searchText = (item) => {
      if (item.nummer.toString().includes(input)) return true;
    };
    if (!!list) {
      list = list.filter((value) => searchText(value));
      return list;
    } else return list;
  }

  /**
   * Wild card search on email
   * @param {Number | String} input - any value to search
   * @param {Array} list - array for search
   * @return {Array} array of object contained keyword
   */
  static wildCardSearchOnEmail(list, input) {
    const searchText = (item) => {
      if (item.email.toString().toUpperCase().indexOf(input.toString().toUpperCase()) !== -1) {
        return true;
      }
    };
    if (!!list) {
      list = input.length > 0 ? list.filter((value) => searchText(value)) : list;
      return list;
    } else return list;
  }

  /**
   * Wild card search on customers email, first and lastname, address, zipcode and city
   * @param {Number | String} input - any value to search
   * @param {Array} list - array for search
   * @return {Array} array of object contained keyword
   */
  static wildCardSearchOnCustomer(list, input) {
    const searchText = (item) => {
      if (!!item.customerEmail && item.customerEmail.toLowerCase().includes(input.toLowerCase())) return true;
      if (!!item.customerName && item.customerName.toLowerCase().includes(input.toLowerCase())) return true;
      if (!!item.customerAddress && item.customerAddress.toLowerCase().includes(input.toLowerCase())) return true;
      if (!!item.customerId && item.customerId.toString().toLowerCase().includes(input.toLowerCase())) return true;
    };
    list = input.length > 0 ? list.filter((value) => searchText(value)) : list;
    return list;
  }

  static wildCardSearchOnSnelstartCustomer(list, input) {
    const searchText = (item) => {
      if (!!item.email && item.email.toLowerCase().includes(input.toLowerCase())) return true;
      if (!!item.naam && item.naam.toLowerCase().includes(input.toLowerCase())) return true;
      if (!!item.relatiecode && item.relatiecode.toString().includes(input.toLowerCase())) return true;
    };
    list = input.length > 0 ? list.filter((value) => searchText(value)) : list;
    return list;
  }

  static wildCardSearchOnExactCustomer(list, input) {
    const searchText = (item) => {
      if (!!item.AccountCode && item.AccountCode.toLowerCase().includes(input.toLowerCase())) return true;
      if (!!item.AccountName && item.AccountName.toLowerCase().includes(input.toLowerCase())) return true;
      if (!!item.EmailAddress && item.EmailAddress.toLowerCase().includes(input.toLowerCase())) return true;
    };
    list = input.length > 0 ? list.filter((value) => searchText(value)) : list;
    return list;
  }

  /**
   * Wild card search on customers email, first and lastname, address, zipcode and city
   * @param {Array} list - array for search
   * @param {Number | String} input - any value to search
   * @return {Array} array of object contained keyword
   */
  static wildCardSearchOnUser(list, input) {
    const searchText = (item) => {
      // Check first name and last name with null/undefined protection
      const firstName = item.userFirstName || "";
      const lastName = item.userLastName || "";
      const name = firstName + " " + lastName;
      if (name.toLowerCase().includes(input.toLowerCase())) return true;

      // Check email with null/undefined protection
      if (item.userEmail && item.userEmail.toLowerCase().includes(input.toLowerCase())) return true;

      // Check customer name with null/undefined protection
      if (item.userCustomerName && item.userCustomerName.toLowerCase().includes(input.toLowerCase())) return true;

      // Check app version with null/undefined protection
      if (item.userAppVersion && item.userAppVersion.toString().toLowerCase().includes(input.toLowerCase()))
        return true;

      return false;
    };

    if (list && input.length > 0) {
      return list.filter((value) => searchText(value));
    }
    return list || [];
  }

  /**
   * Get Breakpoint
   * @param {Object} screens - Grid.useBreakpoint() from antd
   * @return {Array} array of breakpoint size
   */
  static getBreakPoint(screens) {
    let breakpoints = [];
    for (const key in screens) {
      if (screens.hasOwnProperty(key)) {
        const element = screens[key];
        if (element) {
          breakpoints.push(key);
        }
      }
    }
    return breakpoints;
  }

  /**
   * Remove undefined values from object (recursively)
   * Replaces undefined with null for Firestore compatibility
   * @param {*} obj - Object, Array, or primitive value
   * @return {*} Cleaned object with null instead of undefined
   */
  static removeUndefined(obj) {
    if (obj === null || obj === undefined) {
      return null;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(Utils.removeUndefined);
    }
    
    if (typeof obj === 'object') {
      const cleaned = {};
      Object.keys(obj).forEach((key) => {
        const value = obj[key];
        if (value === undefined) {
          cleaned[key] = null;
        } else if (typeof value === 'object' && value !== null) {
          cleaned[key] = Utils.removeUndefined(value);
        } else {
          cleaned[key] = value;
        }
      });
      return cleaned;
    }
    
    return obj;
  }

  /**
   * Parse WhatsApp formatting: *bold*, _italic_, _*bold+italic*_
   * @param {String} text - Text with WhatsApp formatting
   * @return {Array|String} Array of React elements or original text
   */
  static parseWhatsAppFormatting(text) {
    if (!text) return text;

    const parts = [];
    let currentIndex = 0;
    let keyCounter = 0;

    // Combined regex to match all formatting patterns
    // Matches: _*text*_, *text*, _text_
    const regex = /(_\*([^*]+)\*_)|(\*([^*]+)\*)|(_([^_]+)_)/g;
    let match;

    while ((match = regex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > currentIndex) {
        parts.push(text.substring(currentIndex, match.index));
      }

      // Determine which pattern matched and apply appropriate styling
      if (match[1]) {
        // _*text*_ - bold and italic
        parts.push(
          <span key={`format-${keyCounter++}`} style={{ fontWeight: "bold", fontStyle: "italic" }}>
            {match[2]}
          </span>
        );
      } else if (match[3]) {
        // *text* - bold
        parts.push(
          <span key={`format-${keyCounter++}`} style={{ fontWeight: "bold" }}>
            {match[4]}
          </span>
        );
      } else if (match[5]) {
        // _text_ - italic
        parts.push(
          <span key={`format-${keyCounter++}`} style={{ fontStyle: "italic" }}>
            {match[6]}
          </span>
        );
      }

      currentIndex = match.index + match[0].length;
    }

    // Add remaining text after last match
    if (currentIndex < text.length) {
      parts.push(text.substring(currentIndex));
    }

    return parts.length > 0 ? parts : text;
  }
}

export default Utils;
